import React, { useCallback, useRef, useState } from 'react';
import { Marker } from 'react-map-gl';
import { useSelector } from 'react-redux';

import MapPopup, { IProps as IMapPopupProps } from '@/components/route/search/MapPopup/MapPopup';
import MapMarker, { MapMarkerTypes } from '@/components/route/search/SearchMapMarker/MapMarker';
import { useSearchResultsCtx } from '@/components/route/search/SearchResults/SearchResults.context';
import { useWishlistResultsCtx } from '@/components/wishlists/WishlistDetails/WishlistResults.context';
import { TCurrency } from '@/config/locales';
import { EPage, PAGE_PATHNAME } from '@/constants/pages';
import { useMapPopupPosition } from '@/hooks/useMapPopupPosition';
import { useRouter } from '@/hooks/useRouter';
import { getFlexibleDateRangeSelected } from '@/redux/selectors/queryParams';
import { IMapMarkerData } from '@/redux/selectors/search/map';
import { trackListingSelectedEvent } from '@/services/analytics/listings';
import { EListingSource } from '@/services/analytics/listings/types';
import { TFlexibleDateRange } from '@/services/analytics/search/types';
import { formatCurrency } from '@/utility/currency';
import {
  isCampgroundTile,
  ISearchResultTile,
  mapCampgroundTileToRentalPrice,
} from '@/utility/mapSearchResultToTile';
import { preventScrollOnParent } from '@/utility/preventScrollOnParent';
import { roundRatingNumber } from '@/utility/reviews';

type TMapPopupListingsProp = IMapPopupProps['listings'];

const mapListingToPopup = (
  rental: ISearchResultTile,
  index: number,
  flexibleDateRangeSelected: TFlexibleDateRange,
  promoSlug?: string,
  isEscapesSearchPage?: boolean,
): TMapPopupListingsProp[number] | null => {
  const { title, linkUrl, photoUrl } = rental;

  const isCampground = isCampgroundTile(rental);

  if (!title || !linkUrl || !photoUrl || !rental.details || !rental.price) {
    return null;
  }

  const year = (isCampground ? 0 : rental.details.year) || '';
  const classText = (isCampground ? '' : rental.details.classText) || '';
  const length = (isCampground ? '' : rental.details.feet) || '';
  // enforce single decimal digit and hide decimal if a whole number
  const fixedRating = rental.details.ratingNumber
    ? roundRatingNumber(rental.details.ratingNumber)
    : undefined;
  const rating =
    fixedRating && rental.details.ratingQtd
      ? { value: fixedRating, quantity: rental.details.ratingQtd }
      : undefined;
  const handleClick = () => {
    trackListingSelectedEvent({
      rental,
      eventSource: promoSlug ? EListingSource.PROMO : EListingSource.MAP_PREVIEW,
      listingPlacement: index + 1,
      flexibleDateRange: flexibleDateRangeSelected,
      promoSource: promoSlug || null,
    });
  };
  const infos = (isCampground ? [rental.sitesDisplayText || ''] : rental.details.infos) || [];
  const price = isCampground ? mapCampgroundTileToRentalPrice(rental) : rental.price;

  return {
    id: rental.id,
    title,
    price,
    href:
      isEscapesSearchPage && 'escapeLinkUrl' in rental && rental.escapeLinkUrl
        ? rental.escapeLinkUrl
        : linkUrl,
    image: {
      alt: '',
      // @ts-expect-error fixable: unchecked index access
      src: Array<string>().concat(photoUrl)[0],
    },
    year,
    classText,
    length,
    rating,
    infos,
    isInstantBook: isCampground ? false : !!rental.instantBook,
    isUnavailable: isCampground ? false : !!rental.unavailable,
    isSuperhost: isCampground
      ? false
      : !!rental.details.tags?.some(tag => tag.slug === 'superhost'),
    onClick: handleClick,
    isStay: isCampground ? false : !!rental.isStay,
    isCampground,
    isFavorite: rental.isFavorite,
    rental,
  };
};

type TProps = IMapMarkerData & {
  isPopupVisible: boolean;
  container: Element | null;
  currency: TCurrency;
  onClick?: (id: number) => void;
  promoSlug?: string;
  isOnWishlist?: boolean;
};

const SearchMapMarker: React.FC<TProps> = ({
  isPopupVisible,
  container,
  currency,
  lat,
  lon,
  rentals,
  onClick,
  promoSlug,
  isOnWishlist,
}) => {
  const markerRef = useRef<HTMLDivElement>(null);
  // We use `useState` here because we want to force a rerender when we get the popup ref
  const [popupRef, setPopupRef] = useState<HTMLDivElement | null>(null);
  const popupStyle = useMapPopupPosition({
    container,
    anchor: markerRef.current,
    popup: popupRef,
    isVisible: isPopupVisible,
  });
  const router = useRouter();
  const isEscapesSearchPage = router?.pathname?.includes(PAGE_PATHNAME[EPage.ESCAPES_SEARCH]);

  const { hoveredId: searchHoveredId, updateMapHoveredId: updateSearchMapHoveredId } =
    useSearchResultsCtx();
  const { hoveredId: wishlistHoveredId, updateMapHoveredId: updateWishlistHoveredId } =
    useWishlistResultsCtx();
  const flexibleDateRangeSelected = useSelector(getFlexibleDateRangeSelected);

  const rentalId = rentals[0]?.id || 0;
  const isHovered = isOnWishlist
    ? rentals.some(r => wishlistHoveredId === r.id)
    : rentals.some(r => searchHoveredId === r.id);

  const handleOnClick = useCallback<(e: React.MouseEvent<HTMLButtonElement>) => void>(
    e => {
      e.stopPropagation();
      onClick?.(rentalId);
    },
    [rentalId, onClick],
  );

  const handleOnMouseEnter = () => {
    if (isOnWishlist) {
      updateWishlistHoveredId(rentalId);
    } else {
      updateSearchMapHoveredId(rentalId);
    }
  };

  const handleOnMouseLeave = () => {
    if (isOnWishlist) {
      updateWishlistHoveredId(null);
    } else {
      updateSearchMapHoveredId(null);
    }
  };

  // Prevent touch scroll on popup to trigger scroll on body
  const previousBodyOverflow = useRef<string | null>(null);
  const handleTouchStart = useCallback(() => {
    previousBodyOverflow.current = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
  }, []);
  const handleTouchEnd = useCallback(() => {
    if (typeof previousBodyOverflow.current === 'string') {
      window.document.body.style.overflow = previousBodyOverflow.current;
      previousBodyOverflow.current = null;
    }
  }, []);

  const listings = rentals
    .map((rental, idx) =>
      mapListingToPopup(rental, idx, flexibleDateRangeSelected, promoSlug, isEscapesSearchPage),
    )
    .filter(Boolean) as TMapPopupListingsProp;
  const listingsCount = listings.length;

  if (!listingsCount) {
    return null;
  }

  const isStay = listings.some(rental => rental.isStay);

  const isGroup = listingsCount > 1;
  // @ts-expect-error fixable: unchecked index access
  const [{ isInstantBook, isUnavailable, price }] = listings;

  return (
    <Marker style={{ zIndex: isPopupVisible || isHovered ? 10 : 0 }} latitude={lat} longitude={lon}>
      <div className="relative" ref={markerRef}>
        {isPopupVisible && (
          <MapPopup
            ref={setPopupRef}
            style={popupStyle}
            currency={currency}
            listings={listings}
            onWheelCapture={preventScrollOnParent}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            isOnWishlist={isOnWishlist}
          />
        )}

        <MapMarker
          type={isGroup ? MapMarkerTypes.GROUP : MapMarkerTypes.SINGLE}
          isInstantBook={isGroup ? false : isInstantBook}
          isUnavailable={isGroup ? false : isUnavailable}
          isStay={isStay}
          isActive={isPopupVisible || isHovered}
          onClick={handleOnClick}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
          isOnWishlist={isOnWishlist}>
          {isGroup
            ? listingsCount
            : formatCurrency({
                currency,
                digits: 0,
                maxDigits: 0,
                priceInCents:
                  price.estimatedPriceBreakdown?.total ?? price.currentPrice ?? price.originalPrice,
              })}
        </MapMarker>
      </div>
    </Marker>
  );
};

export default React.memo(SearchMapMarker);
